import type { SidecarRecord } from '@/types/intake';

/**
 * Six headline metrics for the Review stats strip, computed over the ACTIVE (non-sent) set the
 * sections render. Pure: the caller passes the same filtered records the worklist shows, so the
 * tiles and the sections stay in lockstep.
 *
 * - pos / neg / untestable: by clinical result.
 * - missingConsent: records flagged noConsentForm. A missing consent does NOT hold the letter or fax;
 *   it only opens background consent research (Member Support). The letter/fax still go out.
 * - pcpLetters / memberLetters: every resulted Positive or Negative generates both a PCP report and a
 *   member result letter, REGARDLESS of consent. Unable-to-test generates a re-do kit, not a letter, so
 *   it never counts toward either letter metric.
 */
export interface ReviewStats {
  pos: number;
  neg: number;
  missingConsent: number;
  untestable: number;
  pcpLetters: number;
  memberLetters: number;
}

export function computeReviewStats(records: SidecarRecord[]): ReviewStats {
  const stats: ReviewStats = {
    pos: 0,
    neg: 0,
    missingConsent: 0,
    untestable: 0,
    pcpLetters: 0,
    memberLetters: 0,
  };
  for (const r of records) {
    const noConsent = !!r.noConsentForm;
    if (r.result === 'Positive') stats.pos += 1;
    else if (r.result === 'Negative') stats.neg += 1;
    else if (r.result === 'Unable to test') stats.untestable += 1;
    if (noConsent) stats.missingConsent += 1;
    // A letter (member + PCP) goes out for every resulted Pos/Neg. Missing consent does NOT hold it -
    // it only opens background consent research; the letter/fax still mail. (Unable-to-test -> re-do kit.)
    const resulted = r.result === 'Positive' || r.result === 'Negative';
    if (resulted) {
      stats.pcpLetters += 1;
      stats.memberLetters += 1;
    }
  }
  return stats;
}
